import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import mapboxgl from "mapbox-gl";
import { bboxFromBoundary } from "../../utils/boundaryUtils";
import { Project, ProjectBoundary } from "../../types/project";
import { ExpandDirection, ExpandPayload } from "../../api/project";

interface Props {
  map: { getCanvas(): HTMLCanvasElement; project(lngLat: [number, number]): { x: number; y: number }; on(e: string, fn: () => void): void; off(e: string, fn: () => void): void } | null;
  boundary: ProjectBoundary | null | undefined;
  project: Project | null | undefined;
  pendingTiles: Record<string, ExpandPayload>;
  onExpand: (payload: ExpandPayload) => void;
}

interface AnchorDef {
  lngLat: [number, number];
  direction: ExpandDirection;
  tile_col: number;
  tile_row: number;
  label: string;
  key: string;
  tileBounds: { tL: number; tB: number; tR: number; tT: number };
}

const METERS_PER_DEG_LAT = 111_000;

function metersToDegreesLat(meters: number) {
  return meters / METERS_PER_DEG_LAT;
}

function metersToDegreesLng(meters: number, latDeg: number) {
  return meters / (METERS_PER_DEG_LAT * Math.cos((latDeg * Math.PI) / 180));
}

function pointInRing(px: number, py: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInBoundary(px: number, py: number, boundary: ProjectBoundary): boolean {
  if (boundary.type === "Polygon") {
    const rings = boundary.coordinates as number[][][];
    if (!pointInRing(px, py, rings[0])) return false;
    for (let h = 1; h < rings.length; h++) {
      if (pointInRing(px, py, rings[h])) return false;
    }
    return true;
  }
  const polys = boundary.coordinates as number[][][][];
  for (const poly of polys) {
    if (!pointInRing(px, py, poly[0])) continue;
    let inHole = false;
    for (let h = 1; h < poly.length; h++) {
      if (pointInRing(px, py, poly[h])) { inHole = true; break; }
    }
    if (!inHole) return true;
  }
  return false;
}

interface TileOverlay {
  polygonPoints: string;
  centerX: number;
  centerY: number;
  key: string;
  /** The tile's top-left corner on screen, where its status sits. */
  cornerX: number;
  cornerY: number;
  /** On-screen width of the tile, to fit (or hide) the status. */
  widthPx: number;
  label: string;
  since: string | null;
  failed: boolean;
}

/** A tile being expanded: where it is and what it's doing. */
interface ExpandingTile {
  col: number;
  row: number;
  label: string;
  since: string | null;
  failed: boolean;
}

/** Narrower than this on screen (zoomed out), a tile shows no status text. */
const MIN_LABEL_TILE_PX = 110;

/** "12 s", "3 min 5 s", "14 min": how long the tile has been at this step. */
function formatElapsed(since: string | null, now: number): string | null {
  if (!since) return null;
  const seconds = Math.max(0, Math.floor((now - Date.parse(since)) / 1000));
  if (!Number.isFinite(seconds) || seconds < 3) return null;
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  return minutes < 10 ? `${minutes} min ${seconds % 60} s` : `${minutes} min`;
}

const ProjectExpandButtons: React.FC<Props> = ({
  map,
  boundary,
  project,
  pendingTiles,
  onExpand,
}) => {
  const [overlays, setOverlays] = useState<TileOverlay[]>([]);
  // Ticks while a tile shows a clock, so the elapsed time counts up between polls.
  const [now, setNow] = useState(() => Date.now());
  const [hoveredPreviewPoints, setHoveredPreviewPoints] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingAnchorRef = useRef<AnchorDef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const anchorsRef = useRef<AnchorDef[]>([]);
  const hoveredKeyRef = useRef<string | null>(null);
  const onExpandRef = useRef(onExpand);
  useEffect(() => { onExpandRef.current = onExpand; }, [onExpand]);

  const handleConfirmExpand = () => {
    const anchor = pendingAnchorRef.current;
    if (anchor) {
      onExpandRef.current({ direction: anchor.direction, tile_col: anchor.tile_col, tile_row: anchor.tile_row });
    }
    pendingAnchorRef.current = null;
    setConfirmOpen(false);
  };

  const handleCancelExpand = () => {
    pendingAnchorRef.current = null;
    setConfirmOpen(false);
  };

  const computeAnchors = useCallback((): AnchorDef[] => {
    const tileWm = project?.tile_width_m;
    const tileHm = project?.tile_height_m;
    const originLng = project?.origin_lng;
    const originLat = project?.origin_lat;

    // --- Origin-based path: always used when origin is set ---
    // Indices sent to the backend are always relative to this fixed origin, so
    // anchors must use the same coordinate system regardless of whether the
    // explicit tile list has been populated yet.
    if (originLng != null && originLat != null && tileWm && tileHm) {
      const tileWidthDeg  = metersToDegreesLng(tileWm, originLat);
      const tileHeightDeg = metersToDegreesLat(tileHm);

      // Build the occupied tile set. Prefer the explicit tile list from the
      // backend; fall back to inferring from the boundary geometry using
      // origin-relative (col, row) indices when the list hasn't been seeded yet.
      const confirmedTiles = project?.tiles;
      let allTiles: { col: number; row: number }[];

      if (confirmedTiles && confirmedTiles.length > 0) {
        allTiles = [...confirmedTiles];
      } else if (boundary) {
        // Infer occupied cells from boundary, but compute indices relative to
        // the fixed origin so they match what the backend expects.
        const bb = bboxFromBoundary(boundary);
        if (!bb) return [];
        const minCol = Math.round((bb.left   - originLng) / tileWidthDeg);
        const maxCol = Math.round((bb.right  - originLng) / tileWidthDeg);
        const minRow = Math.round((bb.bottom - originLat) / tileHeightDeg);
        const maxRow = Math.round((bb.top    - originLat) / tileHeightDeg);
        allTiles = [];
        for (let col = minCol; col < maxCol; col++) {
          for (let row = minRow; row < maxRow; row++) {
            const cx = originLng + (col + 0.5) * tileWidthDeg;
            const cy = originLat + (row + 0.5) * tileHeightDeg;
            if (pointInBoundary(cx, cy, boundary)) allTiles.push({ col, row });
          }
        }
      } else {
        return [];
      }

      // Merge pending tiles so anchors reflect in-flight expansions immediately.
      const occupied = new Set(allTiles.map(t => `${t.col},${t.row}`));
      for (const p of Object.values(pendingTiles)) {
        const k = `${p.tile_col},${p.tile_row}`;
        if (!occupied.has(k)) { occupied.add(k); allTiles.push({ col: p.tile_col, row: p.tile_row }); }
      }
      const isOccupied = (col: number, row: number) => occupied.has(`${col},${row}`);

      const result: AnchorDef[] = [];
      const seen = new Set<string>();
      for (const { col, row } of allTiles) {
        const cellLeft   = originLng + col * tileWidthDeg;
        const cellBottom = originLat + row * tileHeightDeg;
        const midX = cellLeft   + tileWidthDeg  / 2;
        const midY = cellBottom + tileHeightDeg / 2;
        const push = (lngLat: [number, number], dir: ExpandDirection, tc: number, tr: number, label: string) => {
          const key = `${dir}-${tc}-${tr}`;
          if (seen.has(key)) return;
          seen.add(key);
          const tL = originLng + tc * tileWidthDeg;
          const tB = originLat + tr * tileHeightDeg;
          result.push({ lngLat, direction: dir, tile_col: tc, tile_row: tr, label, key,
            tileBounds: { tL, tB, tR: tL + tileWidthDeg, tT: tB + tileHeightDeg } });
        };
        if (!isOccupied(col, row + 1)) push([midX, cellBottom + tileHeightDeg], "north", col, row + 1, "Expand North");
        if (!isOccupied(col, row - 1)) push([midX, cellBottom],                  "south", col, row - 1, "Expand South");
        if (!isOccupied(col + 1, row)) push([cellLeft + tileWidthDeg, midY],     "east",  col + 1, row, "Expand East");
        if (!isOccupied(col - 1, row)) push([cellLeft, midY],                    "west",  col - 1, row, "Expand West");
      }
      return result;
    }

    // --- Pure legacy fallback: no origin set, pre-migration projects only ---
    // These projects still use the old backend which anchors tiles to the
    // current boundary edges, so boundary-relative indices are correct here.
    if (!boundary || !tileWm || !tileHm) return [];
    const bb = bboxFromBoundary(boundary);
    if (!bb) return [];
    const { top, left, bottom, right } = bb;
    const midLat = (top + bottom) / 2;
    const totalWidthDeg = right - left;
    const totalHeightDeg = top - bottom;
    const tileWidthDeg  = metersToDegreesLng(tileWm, midLat);
    const tileHeightDeg = metersToDegreesLat(tileHm);
    const nCols = Math.max(1, Math.round(totalWidthDeg  / tileWidthDeg));
    const nRows = Math.max(1, Math.round(totalHeightDeg / tileHeightDeg));
    const legacyOccupied = (col: number, row: number): boolean => {
      if (col < 0 || col >= nCols || row < 0 || row >= nRows) return false;
      return pointInBoundary(left + (col + 0.5) * tileWidthDeg, bottom + (row + 0.5) * tileHeightDeg, boundary);
    };
    const result: AnchorDef[] = [];
    for (let col = 0; col < nCols; col++) {
      for (let row = 0; row < nRows; row++) {
        if (!legacyOccupied(col, row)) continue;
        const cellLeft   = left   + col * tileWidthDeg;
        const cellBottom = bottom + row * tileHeightDeg;
        const midX = cellLeft   + tileWidthDeg  / 2;
        const midY = cellBottom + tileHeightDeg / 2;
        const push = (lngLat: [number, number], dir: ExpandDirection, tc: number, tr: number, label: string) => {
          const tL = left + tc * tileWidthDeg;
          const tB = bottom + tr * tileHeightDeg;
          result.push({ lngLat, direction: dir, tile_col: tc, tile_row: tr, label, key: `${dir}-${tc}-${tr}`,
            tileBounds: { tL, tB, tR: tL + tileWidthDeg, tT: tB + tileHeightDeg } });
        };
        if (!legacyOccupied(col, row + 1)) push([midX, cellBottom + tileHeightDeg], "north", col, row + 1, "Expand North");
        if (!legacyOccupied(col, row - 1)) push([midX, cellBottom],                  "south", col, row - 1, "Expand South");
        if (!legacyOccupied(col + 1, row)) push([cellLeft + tileWidthDeg, midY],     "east",  col + 1, row, "Expand East");
        if (!legacyOccupied(col - 1, row)) push([cellLeft, midY],                    "west",  col - 1, row, "Expand West");
      }
    }
    return result;
  }, [boundary, pendingTiles, project?.tiles, project?.origin_lng, project?.origin_lat, project?.tile_width_m, project?.tile_height_m]);

  const updateHoverPreview = useCallback(() => {
    const key = hoveredKeyRef.current;
    if (!map || !key) { setHoveredPreviewPoints(null); return; }
    const anchor = anchorsRef.current.find(a => a.key === key);
    if (!anchor) { setHoveredPreviewPoints(null); return; }
    const { tL, tB, tR, tT } = anchor.tileBounds;
    const sw = map.project([tL, tB]);
    const se = map.project([tR, tB]);
    const ne = map.project([tR, tT]);
    const nw = map.project([tL, tT]);
    setHoveredPreviewPoints(`${sw.x},${sw.y} ${se.x},${se.y} ${ne.x},${ne.y} ${nw.x},${nw.y}`);
  }, [map]);

  // The tiles to cover: every tile of the batch the server reports (with
  // what it is doing), plus any just clicked that the server hasn't
  // acknowledged yet.
  const expandingTiles = useMemo<ExpandingTile[]>(() => {
    const byKey = new Map<string, ExpandingTile>();
    if (project?.status === "expanding") {
      for (const t of project.tiles ?? []) {
        if (!t.progress) continue;
        byKey.set(`${t.col},${t.row}`, {
          col: t.col,
          row: t.row,
          label: t.progress,
          since: t.progress_since ?? null,
          failed: t.status === "failed",
        });
      }
    }
    for (const p of Object.values(pendingTiles)) {
      const key = `${p.tile_col},${p.tile_row}`;
      if (!byKey.has(key)) byKey.set(key, { col: p.tile_col, row: p.tile_row, label: "Queued", since: null, failed: false });
    }
    return Array.from(byKey.values());
  }, [project?.status, project?.tiles, pendingTiles]);

  // Compute screen-space overlays for the expanding tiles
  const computeOverlays = useCallback(() => {
    if (!map || expandingTiles.length === 0) {
      setOverlays([]);
      return;
    }

    const originLng = project?.origin_lng;
    const originLat = project?.origin_lat;
    const tileWm = project?.tile_width_m;
    const tileHm = project?.tile_height_m;

    // Helper: project a tile rect to screen-space overlay
    const tileToOverlay = (tL: number, tB: number, tR: number, tT: number, tile: ExpandingTile): TileOverlay => {
      const sw = map.project([tL, tB]);
      const se = map.project([tR, tB]);
      const ne = map.project([tR, tT]);
      const nw = map.project([tL, tT]);
      const polygonPoints = `${sw.x},${sw.y} ${se.x},${se.y} ${ne.x},${ne.y} ${nw.x},${nw.y}`;
      const center = map.project([(tL + tR) / 2, (tB + tT) / 2]);
      return {
        polygonPoints,
        centerX: center.x,
        centerY: center.y,
        key: `${tile.col},${tile.row}`,
        cornerX: nw.x,
        cornerY: nw.y,
        widthPx: Math.hypot(ne.x - nw.x, ne.y - nw.y),
        label: tile.label,
        since: tile.since,
        failed: tile.failed,
      };
    };

    const newOverlays: TileOverlay[] = [];

    if (originLng != null && originLat != null && tileWm && tileHm) {
      // New projects: fixed reference latitude — tile_width_deg never drifts
      const tileWidthDeg  = metersToDegreesLng(tileWm, originLat);
      const tileHeightDeg = metersToDegreesLat(tileHm);
      expandingTiles.forEach((tile) => {
        const tL = originLng + tile.col * tileWidthDeg;
        const tB = originLat + tile.row * tileHeightDeg;
        newOverlays.push(tileToOverlay(tL, tB, tL + tileWidthDeg, tB + tileHeightDeg, tile));
      });
    } else if (boundary && tileWm && tileHm) {
      // Legacy projects without origin: compute from current boundary bbox.
      // Note: south/west expansions may jump one tile after the boundary updates,
      // but this only affects projects created before the tile-list migration.
      const bb = bboxFromBoundary(boundary);
      if (bb) {
        const { top, left, bottom } = bb;
        const midLat = (top + bottom) / 2;
        const tileWidthDeg  = metersToDegreesLng(tileWm, midLat);
        const tileHeightDeg = metersToDegreesLat(tileHm);
        expandingTiles.forEach((tile) => {
          const tL = left + tile.col * tileWidthDeg;
          const tB = bottom + tile.row * tileHeightDeg;
          newOverlays.push(tileToOverlay(tL, tB, tL + tileWidthDeg, tB + tileHeightDeg, tile));
        });
      }
    }

    setOverlays(newOverlays);
  }, [map, boundary, expandingTiles, project?.origin_lng, project?.origin_lat, project?.tile_width_m, project?.tile_height_m]);

  const hasClock = overlays.some((o) => o.since);
  useEffect(() => {
    if (!hasClock) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasClock]);

  // Recreate flat-on-map markers whenever anchors or map changes
  useEffect(() => {
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (!map) return;
    const newAnchors = computeAnchors();
    anchorsRef.current = newAnchors;
    newAnchors.forEach(anchor => {
      const el = document.createElement("div");
      el.title = anchor.label;
      el.style.cssText = [
        "width:36px", "height:36px", "border-radius:50%",
        "background:rgba(0,0,0,0.25)", "border:2px dashed rgba(0,212,173,0.8)",
        "display:flex", "align-items:center", "justify-content:center",
        "cursor:pointer", "backdrop-filter:blur(2px)",
      ].join(";");
      el.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22"><path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="rgba(0,212,173,0.9)"/></svg>`;
      el.addEventListener("click", () => {
        pendingAnchorRef.current = anchor;
        setConfirmOpen(true);
      });
      el.addEventListener("mouseenter", () => { hoveredKeyRef.current = anchor.key; updateHoverPreview(); });
      el.addEventListener("mouseleave", () => { hoveredKeyRef.current = null; setHoveredPreviewPoints(null); });
      const marker = new mapboxgl.Marker({ element: el, pitchAlignment: "map", rotationAlignment: "map", anchor: "center" })
        .setLngLat(anchor.lngLat)
        .addTo(map as unknown as mapboxgl.Map);
      markersRef.current.push(marker);
    });
    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
    };
  }, [map, computeAnchors, updateHoverPreview]);

  // Keep hover preview polygon in sync as the map moves
  useEffect(() => {
    if (!map) return;
    map.on("move", updateHoverPreview);
    return () => { map.off("move", updateHoverPreview); };
  }, [map, updateHoverPreview]);

  useEffect(() => {
    if (!map) return;
    computeOverlays();
    map.on("move", computeOverlays);
    map.on("zoom", computeOverlays);
    map.on("resize", computeOverlays);
    return () => {
      map.off("move", computeOverlays);
      map.off("zoom", computeOverlays);
      map.off("resize", computeOverlays);
    };
  }, [map, computeOverlays]);

  // Attach container to map canvas parent
  useEffect(() => {
    if (!map) return;
    const parent = map.getCanvas().parentElement;
    if (!parent) return;
    if (!containerRef.current) {
      const div = document.createElement("div");
      div.style.cssText = "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;";
      parent.appendChild(div);
      containerRef.current = div;
    }
    return () => {
      if (containerRef.current && containerRef.current.parentElement) {
        containerRef.current.parentElement.removeChild(containerRef.current);
        containerRef.current = null;
      }
    };
  }, [map]);

  if (!containerRef.current) return null;

  return createPortal(
    <>
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
        {overlays.map(({ polygonPoints, key, failed }) => (
          <polygon
            key={`poly-${key}`}
            points={polygonPoints}
            fill={failed ? "rgba(127,29,29,0.45)" : "rgba(0,0,0,0.45)"}
          />
        ))}
        {hoveredPreviewPoints && (
          <polygon
            points={hoveredPreviewPoints}
            fill="rgba(0,212,173,0.08)"
            stroke="rgba(0,212,173,0.7)"
            strokeWidth={2}
            strokeDasharray="7 4"
          />
        )}
      </svg>
      {overlays.filter((o) => !o.failed).map(({ centerX, centerY, key }) => (
        <div key={`spinner-${key}`} style={{
          position: "absolute",
          left: centerX - 24,
          top: centerY - 24,
          pointerEvents: "none",
        }}>
          <CircularProgress size={48} sx={{ color: "rgba(0,212,173,0.9)" }} />
        </div>
      ))}
      {/* What each tile is doing, in its top-left corner, with how long it
          has been at it. Hidden when the tile is too small on screen. */}
      {overlays
        .filter((o) => o.widthPx >= MIN_LABEL_TILE_PX)
        .map(({ cornerX, cornerY, widthPx, key, label, since, failed }) => {
          const elapsed = formatElapsed(since, now);
          return (
            <div
              key={`status-${key}`}
              role="status"
              style={{
                position: "absolute",
                left: cornerX + 8,
                top: cornerY + 8,
                maxWidth: Math.min(widthPx - 16, 260),
                pointerEvents: "none",
                padding: "4px 8px",
                borderRadius: 6,
                backgroundColor: failed ? "rgba(127,29,29,0.92)" : "rgba(15,23,42,0.88)",
                border: `1px solid ${failed ? "rgba(248,113,113,0.6)" : "rgba(0,212,173,0.45)"}`,
                color: "#f8fafc",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.35,
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              }}
            >
              {label}
              {elapsed && <span style={{ fontWeight: 400, opacity: 0.75 }}> · {elapsed}</span>}
            </div>
          );
        })}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelExpand}
        PaperProps={{ sx: { backgroundColor: "#1e1e1e", color: "#fff" } }}
      >
        <DialogTitle>Confirm Project Expansion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.7)" }}>
            Are you sure you want to expand the project in this direction?
            This will add a new tile and may increase processing time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExpand} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmExpand} variant="contained" sx={{ backgroundColor: "#00d4ad" }}>
            Expand
          </Button>
        </DialogActions>
      </Dialog>
    </>,
    containerRef.current
  );
};

export default ProjectExpandButtons;
